import {
  MOCK_ANALYTICS,
  MOCK_BUY_REQUESTS,
  MOCK_CARRIERS,
  MOCK_CATEGORIES,
  MOCK_CONVERSATIONS,
  MOCK_HS_CODES,
  MOCK_LEADS,
  MOCK_NOTIFICATIONS,
  MOCK_ORDERS,
  MOCK_PRODUCTS,
  MOCK_PROFILE,
  MOCK_QUOTES,
  MOCK_TOKENS,
  MOCK_USER,
  appendMessage,
  getBuyRequest,
  getConversation,
  getLead,
  getMessages,
  getOrder,
  getShipment,
  getUnreadNotificationsCount,
  getUserBasic,
  markAllNotificationsRead,
  markNotificationRead,
  paginate,
  patchLead,
  updateOrderStatus,
} from './db';

type HandlerFn = (req: Request, url: URL) => Response | Promise<Response>;

interface RouteDef {
  method: string;
  pattern: RegExp;
  handler: HandlerFn;
}

function json(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
}

function empty(status = 204): Response {
  return new Response(null, { status });
}

function pageParams(url: URL): { page: number; size: number } {
  return {
    page: Number(url.searchParams.get('page') ?? 0),
    size: Number(url.searchParams.get('size') ?? 20),
  };
}

function lastPathSegment(url: URL): string {
  return url.pathname.split('/').filter(Boolean).at(-1) ?? '';
}

function segmentAt(url: URL, offsetFromEnd: number): string {
  const parts = url.pathname.split('/').filter(Boolean);
  return parts[parts.length - 1 - offsetFromEnd] ?? '';
}

async function readBody<T>(req: Request): Promise<T> {
  try {
    return (await req.json()) as T;
  } catch {
    return {} as T;
  }
}

const ROUTES: RouteDef[] = [
  // ─── Auth ──────────────────────────────────────────────────────
  { method: 'POST', pattern: /\/auth\/register$/, handler: () =>
    json({ userId: MOCK_USER.id, role: 'PRODUCER', message: 'Verification code sent.' }) },
  { method: 'POST', pattern: /\/auth\/verify$/, handler: () => json(MOCK_TOKENS) },
  { method: 'POST', pattern: /\/auth\/login$/, handler: () => json(MOCK_TOKENS) },
  { method: 'POST', pattern: /\/auth\/otp\/request$/, handler: () => empty() },
  { method: 'POST', pattern: /\/auth\/otp\/login$/, handler: () => json(MOCK_TOKENS) },
  { method: 'POST', pattern: /\/auth\/refresh$/, handler: () => json(MOCK_TOKENS) },
  { method: 'POST', pattern: /\/auth\/logout$/, handler: () => empty() },
  { method: 'POST', pattern: /\/auth\/password\/reset\/request$/, handler: () => empty(202) },
  { method: 'POST', pattern: /\/auth\/password\/reset\/confirm$/, handler: () => empty(200) },

  // ─── Account ──────────────────────────────────────────────────
  { method: 'POST', pattern: /\/account\/password\/change$/, handler: () => empty(200) },

  // ─── Me & user directory ──────────────────────────────────────
  { method: 'GET', pattern: /\/me$/, handler: () => json(MOCK_USER) },
  {
    method: 'GET',
    pattern: /\/users\/basic$/,
    handler: (_req, url) => {
      const ids = (url.searchParams.get('ids') ?? '').split(',').filter(Boolean);
      return json(ids.map(getUserBasic));
    },
  },
  {
    method: 'GET',
    pattern: /\/users\/[0-9a-z-]+\/basic$/i,
    handler: (_req, url) => json(getUserBasic(segmentAt(url, 1))),
  },

  // ─── Profile ──────────────────────────────────────────────────
  { method: 'GET', pattern: /\/manufacturer\/profile$/, handler: () => json(MOCK_PROFILE) },
  { method: 'PUT', pattern: /\/manufacturer\/profile$/, handler: () => json(MOCK_PROFILE) },
  {
    method: 'POST',
    pattern: /\/manufacturer\/profile\/submit-kyc$/,
    handler: () => json({ ...MOCK_PROFILE, kycStatus: 'PENDING' }),
  },

  // ─── Taxonomy ─────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/taxonomy\/categories$/,
    handler: (_req, url) => {
      // Spec contract: ?kind=PRODUCT (single) OR ?kinds=PRODUCER,ECO_PRODUCER (multi).
      const single = url.searchParams.get('kind');
      const multi = url.searchParams.get('kinds');
      const raw = (single ?? multi ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const wanted = new Set(raw);
      const data =
        wanted.size === 0
          ? MOCK_CATEGORIES
          : MOCK_CATEGORIES.filter((c) => wanted.has(c.kind));
      return json(data);
    },
  },
  {
    method: 'GET',
    pattern: /\/taxonomy\/hs-codes/,
    handler: (_req, url) => {
      const q = (url.searchParams.get('q') ?? '').toLowerCase();
      return json(
        MOCK_HS_CODES.filter(
          (c) => !q || c.code.includes(q) || c.description.toLowerCase().includes(q),
        ),
      );
    },
  },

  // ─── Media ────────────────────────────────────────────────────
  {
    method: 'POST',
    pattern: /\/media\/uploads$/,
    handler: () =>
      json({
        mediaId: `media-mock-${Math.floor(Math.random() * 1e9).toString(36)}`,
        uploadUrl: 'about:blank',
        method: 'PUT',
        expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
      }),
  },
  { method: 'POST', pattern: /\/media\/uploads\/.+\/confirm$/, handler: () => empty() },
  {
    method: 'GET',
    pattern: /\/media\/.+\/download$/,
    handler: (_req, url) => {
      const mediaId = segmentAt(url, 1);
      // Stable seeded SVG placeholder so list thumbnails stay distinct.
      const hue = Array.from(mediaId).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360, 0);
      const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'>` +
        `<rect width='80' height='80' fill='hsl(${hue},65%,55%)'/>` +
        `<text x='50%' y='55%' fill='white' font-family='Inter,sans-serif' font-size='12' font-weight='600' text-anchor='middle'>` +
        mediaId.slice(-3).toUpperCase() +
        `</text></svg>`;
      return json({
        downloadUrl: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
        expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
      });
    },
  },
  { method: 'DELETE', pattern: /\/media\/[^/]+$/i, handler: () => empty(204) },

  // ─── Products ─────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/products$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      const status = (url.searchParams.get('status') ?? '').split(',').filter(Boolean);
      const q = (url.searchParams.get('q') ?? '').toLowerCase();
      const category = url.searchParams.get('category');
      const stockOnly = url.searchParams.get('stock') === 'true';
      const filtered = MOCK_PRODUCTS.filter(
        (p) =>
          (status.length === 0 || status.includes(p.status)) &&
          (!q ||
            [p.name.ru, p.name.en, p.name.uz].some((s) =>
              s.toLowerCase().includes(q),
            )) &&
          (!category || p.categoryId === category) &&
          (!stockOnly || p.stock),
      );
      return json(paginate(filtered, page, size));
    },
  },
  {
    method: 'GET',
    pattern: /\/products\/[^/]+$/i,
    handler: (_req, url) => {
      const id = lastPathSegment(url);
      const product = MOCK_PRODUCTS.find((p) => p.id === id) ?? MOCK_PRODUCTS[0]!;
      return json(product);
    },
  },
  { method: 'POST', pattern: /\/products$/, handler: () => json(MOCK_PRODUCTS[0]) },
  { method: 'PUT', pattern: /\/products\/[^/]+$/i, handler: () => json(MOCK_PRODUCTS[0]) },
  { method: 'POST', pattern: /\/products\/[^/]+\/submit$/i, handler: () =>
    json({ ...MOCK_PRODUCTS[0]!, status: 'PENDING_MODERATION' }) },
  { method: 'DELETE', pattern: /\/products\/[^/]+$/i, handler: () => empty() },

  // ─── Buy requests ─────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/buy-requests\/feed$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      const q = (url.searchParams.get('q') ?? '').toLowerCase();
      const category = url.searchParams.get('category') ?? undefined;
      const country = url.searchParams.get('country') ?? undefined;
      const filtered = MOCK_BUY_REQUESTS.filter(
        (r) =>
          (!q || r.name.toLowerCase().includes(q)) &&
          (!category || r.category === category) &&
          (!country || r.destinationCountry === country),
      );
      return json(paginate(filtered, page, size));
    },
  },
  {
    method: 'GET',
    pattern: /\/buy-requests\/[^/]+$/i,
    handler: (_req, url) => {
      const id = lastPathSegment(url);
      const r = getBuyRequest(id);
      if (!r) {
        return json(
          {
            type: 'about:blank',
            title: 'Not Found',
            status: 404,
            detail: 'Buy request not found',
            code: 'NOT_FOUND',
          },
          { status: 404, headers: { 'content-type': 'application/problem+json' } },
        );
      }
      return json(r);
    },
  },
  {
    method: 'POST',
    pattern: /\/buy-requests\/[^/]+\/quote$/i,
    handler: (_req, url) => {
      const id = segmentAt(url, 1);
      const rfq = getBuyRequest(id);
      return json({
        id: `quote-${Date.now()}`,
        buyRequestId: id,
        buyRequestTitle: rfq?.name ?? 'Mock quote',
        manufacturerId: MOCK_PROFILE.id,
        price: { amount: '10000.00', currency: 'USD' },
        leadTimeDays: 30,
        terms: null,
        attachments: [],
        acceptedAt: null,
        withdrawn: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
  },

  // ─── Quotes ───────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/quotes$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      return json(paginate(MOCK_QUOTES, page, size));
    },
  },
  {
    method: 'POST',
    pattern: /\/quotes\/[^/]+\/withdraw$/i,
    handler: (_req, url) => {
      const id = segmentAt(url, 1);
      const q = MOCK_QUOTES.find((x) => x.id === id);
      if (q) q.withdrawn = true;
      return json(q ?? {});
    },
  },

  // ─── Orders ───────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/orders$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      const status = (url.searchParams.get('status') ?? '').split(',').filter(Boolean);
      const filtered = MOCK_ORDERS.filter((o) => status.length === 0 || status.includes(o.status));
      return json(paginate(filtered, page, size));
    },
  },
  {
    method: 'GET',
    pattern: /\/orders\/[^/]+$/i,
    handler: (_req, url) => {
      const id = lastPathSegment(url);
      return json(getOrder(id) ?? MOCK_ORDERS[0]);
    },
  },
  {
    method: 'PATCH',
    pattern: /\/orders\/[^/]+\/status$/i,
    handler: async (req, url) => {
      const id = segmentAt(url, 1);
      const body = await readBody<{ status: string; note?: string | null }>(req);
      const updated =
        updateOrderStatus(id, body.status as never, body.note ?? null) ?? getOrder(id);
      return json(updated);
    },
  },
  {
    method: 'POST',
    pattern: /\/orders\/[^/]+\/documents$/i,
    handler: async (req, url) => {
      const id = segmentAt(url, 1);
      const body = await readBody<{ mediaId: string }>(req);
      const order = getOrder(id);
      if (order) order.documents = [...order.documents, body.mediaId];
      return json(order);
    },
  },
  {
    method: 'DELETE',
    pattern: /\/orders\/[^/]+\/documents\/[^/]+$/i,
    handler: (_req, url) => {
      const mediaId = lastPathSegment(url);
      const orderId = segmentAt(url, 2);
      const order = getOrder(orderId);
      if (order) order.documents = order.documents.filter((m) => m !== mediaId);
      return json(order);
    },
  },

  // ─── Logistics ────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/logistics\/carriers$/,
    handler: () => json(MOCK_CARRIERS),
  },
  {
    method: 'GET',
    pattern: /\/logistics\/shipments\/[^/]+$/i,
    handler: (_req, url) => {
      const orderId = lastPathSegment(url);
      const shipment = getShipment(orderId);
      if (!shipment) {
        return json(
          { type: 'about:blank', title: 'Not Found', status: 404, code: 'NOT_FOUND' },
          { status: 404, headers: { 'content-type': 'application/problem+json' } },
        );
      }
      return json(shipment);
    },
  },
  {
    method: 'POST',
    pattern: /\/logistics\/shipments\/[^/]+\/carrier$/i,
    handler: async (req, url) => {
      const shipmentId = segmentAt(url, 1);
      const body = await readBody<{ carrierId: string; trackingNumber?: string | null; eta?: string | null }>(req);
      const carrier = MOCK_CARRIERS.find((c) => c.id === body.carrierId) ?? null;
      // Shipment IDs in the seed are `shipment-<orderId>` — strip prefix to find it.
      const orderId = shipmentId.replace(/^shipment-/, '');
      const existing = getShipment(orderId);
      if (!existing) {
        return json({ type: 'about:blank', status: 404 }, { status: 404 });
      }
      existing.carrier = carrier;
      existing.trackingNumber = body.trackingNumber ?? null;
      existing.etaDate = body.eta ?? null;
      existing.updatedAt = new Date().toISOString();
      return json(existing);
    },
  },
  {
    method: 'POST',
    pattern: /\/logistics\/quote$/,
    handler: () =>
      json([
        { carrierName: 'DHL Global Forwarding', price: { amount: '4200.00', currency: 'USD' }, estimatedDays: 14 },
        { carrierName: 'FedEx Trade Networks', price: { amount: '4850.00', currency: 'USD' }, estimatedDays: 12 },
        { carrierName: 'Maersk Ocean', price: { amount: '2900.00', currency: 'USD' }, estimatedDays: 28 },
      ]),
  },

  // ─── CRM ──────────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/crm\/leads$/,
    handler: (_req, url) => {
      const status = url.searchParams.get('status');
      return json(status ? MOCK_LEADS.filter((l) => l.status === status) : MOCK_LEADS);
    },
  },
  {
    method: 'POST',
    pattern: /\/crm\/leads$/,
    handler: async (req) => {
      const body = await readBody<{ title?: string; buyerId?: string | null; notes?: string | null }>(req);
      const created = {
        id: `lead-${Date.now()}`,
        manufacturerId: MOCK_PROFILE.id,
        buyerId: body.buyerId ?? null,
        title: body.title ?? 'Untitled lead',
        notes: body.notes ?? null,
        status: 'NEW' as const,
        source: 'MANUAL' as const,
        sourceRefId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      MOCK_LEADS.unshift(created);
      return json(created);
    },
  },
  {
    method: 'PATCH',
    pattern: /\/crm\/leads\/[^/]+\/status$/i,
    handler: async (req, url) => {
      const id = segmentAt(url, 1);
      const body = await readBody<{ status: 'NEW' | 'IN_TOUCH' | 'QUOTED' | 'WON' | 'LOST' }>(req);
      return json(patchLead(id, { status: body.status }) ?? getLead(id));
    },
  },
  {
    method: 'PATCH',
    pattern: /\/crm\/leads\/[^/]+\/notes$/i,
    handler: async (req, url) => {
      const id = segmentAt(url, 1);
      const body = await readBody<{ notes: string | null }>(req);
      return json(patchLead(id, { notes: body.notes }) ?? getLead(id));
    },
  },
  {
    method: 'PATCH',
    pattern: /\/crm\/leads\/[^/]+\/title$/i,
    handler: async (req, url) => {
      const id = segmentAt(url, 1);
      const body = await readBody<{ title: string }>(req);
      return json(patchLead(id, { title: body.title }) ?? getLead(id));
    },
  },

  // ─── Conversations / messages ─────────────────────────────────
  {
    method: 'GET',
    pattern: /\/conversations$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      return json(paginate(MOCK_CONVERSATIONS, page, size));
    },
  },
  {
    method: 'GET',
    pattern: /\/conversations\/unread-count$/,
    handler: () =>
      json({ count: MOCK_CONVERSATIONS.reduce((sum, c) => sum + c.unreadCount, 0) }),
  },
  {
    method: 'GET',
    pattern: /\/conversations\/[^/]+$/i,
    handler: (_req, url) => {
      const id = lastPathSegment(url);
      return json(getConversation(id) ?? MOCK_CONVERSATIONS[0]);
    },
  },
  {
    method: 'GET',
    pattern: /\/conversations\/[^/]+\/messages$/i,
    handler: (_req, url) => {
      const conversationId = segmentAt(url, 1);
      const { page, size } = pageParams(url);
      const messages = [...getMessages(conversationId)].reverse(); // newest first per API convention
      return json(paginate(messages, page, size));
    },
  },
  {
    method: 'POST',
    pattern: /\/conversations\/[^/]+\/messages$/i,
    handler: async (req, url) => {
      const conversationId = segmentAt(url, 1);
      const body = await readBody<{ body: string; attachments?: string[] }>(req);
      const message = appendMessage(conversationId, body.body ?? '', body.attachments ?? []);
      return json(message ?? {});
    },
  },
  {
    method: 'POST',
    pattern: /\/conversations\/[^/]+\/read$/i,
    handler: (_req, url) => {
      const id = segmentAt(url, 1);
      const conv = getConversation(id);
      if (conv) conv.unreadCount = 0;
      return empty();
    },
  },

  // ─── Notifications ────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/notifications$/,
    handler: (_req, url) => {
      const { page, size } = pageParams(url);
      return json(paginate(MOCK_NOTIFICATIONS, page, size));
    },
  },
  {
    method: 'GET',
    pattern: /\/notifications\/unread-count$/,
    handler: () => json({ count: getUnreadNotificationsCount() }),
  },
  {
    method: 'POST',
    pattern: /\/notifications\/read-all$/,
    handler: () => json({ updated: markAllNotificationsRead() }),
  },
  {
    method: 'POST',
    pattern: /\/notifications\/[^/]+\/read$/i,
    handler: (_req, url) => {
      markNotificationRead(segmentAt(url, 1));
      return empty();
    },
  },

  // ─── Analytics ────────────────────────────────────────────────
  {
    method: 'GET',
    pattern: /\/analytics\/dashboard/,
    handler: (_req, url) => {
      const range = url.searchParams.get('range') ?? 'WEEK';
      return json({ ...MOCK_ANALYTICS, range });
    },
  },
];

export async function tryMockMatch(request: Request): Promise<Response | null> {
  const url = new URL(request.url);
  const match = ROUTES.find(
    (r) => r.method === request.method && r.pattern.test(url.pathname),
  );
  if (!match) {
    console.warn(`[mock] no handler for ${request.method} ${url.pathname} — returning empty 200`);
    return new Response('{}', {
      status: 200,
      headers: { 'content-type': 'application/json' },
    });
  }
  return match.handler(request, url);
}
